import { useMutation, useQuery } from '@tanstack/react-query'
import { MailCheck } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Card, EmptyState, LoadingPanel, Notice } from '@/components/ui/misc'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { api, ApiError } from '@/lib/api'

interface InvitePreview {
  workspaceName: string
  invitedByName: string
  email: string
  expiresAt: string
}

/**
 * The landing page for an invitation link. Sits behind the normal sign-in gate,
 * so an invitee without an account registers first and arrives back here.
 */
export function JoinWorkspace() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const { user } = useCurrentUser()

  const preview = useQuery({
    queryKey: ['workspace-invite', token],
    queryFn: () => api.get<{ invite: InvitePreview }>(`/workspace-invites/${token}`),
    enabled: token.length > 0,
    retry: false,
  })

  const accept = useMutation({
    mutationFn: () =>
      api.post<{ workspaceId: string; workspaceName: string }>(
        `/workspace-invites/${token}/accept`,
      ),
    onSuccess: ({ workspaceId, workspaceName }) => {
      toast.success(`You joined ${workspaceName}`)
      navigate(`/projects/${workspaceId}`, { replace: true })
    },
    onError: (error: ApiError) => toast.error(error.body.error),
  })

  if (!token) {
    return (
      <Centered>
        <EmptyState
          title="No invitation token"
          description="Open the link from your invitation email."
        />
      </Centered>
    )
  }

  if (preview.isLoading) return <LoadingPanel label="Checking your invitation" />

  if (preview.isError) {
    return (
      <Centered>
        <EmptyState
          title="This invitation is not valid"
          description={
            (preview.error as ApiError).body?.error ??
            'The link may have expired or already been used.'
          }
          action={
            <Button variant="secondary" onClick={() => navigate('/projects')}>
              Go to projects
            </Button>
          }
        />
      </Centered>
    )
  }

  const invite = preview.data!.invite
  // The server refuses a mismatch anyway; saying so here saves a wasted click
  // on a shared machine where someone else is still signed in.
  const wrongAccount = Boolean(user) && user!.email.toLowerCase() !== invite.email.toLowerCase()

  return (
    <Centered>
      <Card className="p-8 text-center">
        <MailCheck className="mx-auto size-7 text-brand" />

        <h1 className="pt-3 font-display text-2xl font-semibold text-ink">
          Join {invite.workspaceName}
        </h1>

        <p className="pt-2 text-sm leading-relaxed text-ink-muted">
          {invite.invitedByName} invited <strong>{invite.email}</strong> to collaborate in this
          project. Members can see every record shared with the group, including drafts
          that are not in the Public Database.
        </p>

        {wrongAccount && (
          <div className="pt-4 text-left">
            <Notice tone="caution" title="You are signed in as someone else">
              This invitation was sent to {invite.email}, but you are signed in as {user!.email}.
              Sign out and sign back in with the invited address to accept it.
            </Notice>
          </div>
        )}

        <div className="flex justify-center gap-2 pt-5">
          <Button variant="secondary" onClick={() => navigate('/projects')}>
            Not now
          </Button>
          <Button
            disabled={wrongAccount}
            loading={accept.isPending}
            onClick={() => accept.mutate()}
          >
            Accept invitation
          </Button>
        </div>
      </Card>
    </Centered>
  )
}

const Centered = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto max-w-lg px-5 py-16">{children}</div>
)
